import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import { useMediaQuery } from '@/hooks';
import { useTrialDiscount } from '@/hooks/user';
import Fire from '@/icons/fire.svg';
import Redeem from '@/icons/redeem.svg';
import routes from '@/routes';
import { setPrice } from '@/store/checkout';
import { AUTH_MODAL_TYPES, openAuthModal } from '@/store/profile';
import { cn } from '@/utils';
import { formatPrice, recurringDetailsText } from '@/utils/money';

function PlanCard({ plan }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const loggedIn = useSelector(state => state.profile.loggedIn);
	const mdDown = useMediaQuery('mdDown');
	const trialDiscount = useTrialDiscount();
	const isTrialDiscountVisible = plan.isBestPlan && trialDiscount;
	const textBestPlanFlag = isTrialDiscountVisible
		? t('billing.trialDiscount')
		: t('billing.mostPopular');
	const textPrice = `${formatPrice({
		amount: isTrialDiscountVisible ? 0 : plan.baseAmount,
		currency: plan.currency
	})} ${recurringDetailsText(plan)}`;

	return (
		<div className="flex flex-1 flex-col">
			{plan.isBestPlan && (
				<div className="inline-flex w-full items-center justify-center gap-2 rounded-tl rounded-tr bg-white px-4 py-2 text-red md:w-fit md:bg-red-background">
					{isTrialDiscountVisible ? <Redeem /> : <Fire />}
					<span className="justify-center text-sm font-semibold">
						{textBestPlanFlag}
					</span>
				</div>
			)}

			<div
				className={cn(
					'md:full-w flex flex-1 flex-col items-start justify-start rounded border bg-white',
					plan.isBestPlan
						? 'border-black shadow-[0px_4px_8px_2px_rgba(236,76,56,0.40)]'
						: 'mt-2 border-grey-200 md:mt-[40px]'
				)}
			>
				<div className="flex min-h-[14.25rem] w-full flex-1 flex-col gap-2 border-b border-grey-200 px-6 py-7">
					<h2 className="font-semibold leading-7">
						{t(`plans.${plan.name}.name`)}
					</h2>
					<div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-3">
						<h3 className="font-title text-3xl font-normal leading-10">
							{textPrice}
						</h3>
						{isTrialDiscountVisible && (
							<p className="rounded-[2px] bg-red-background px-2 py-1 text-xs font-semibold leading-5 text-red md:text-base md:leading-7">
								{t(`fastCheckout.trialDiscount`)}
							</p>
						)}
					</div>
					<span className="text-xs font-semibold leading-5 text-grey-500">
						{plan.name !== 'trial' && t(`plans.${plan.name}.periodicity`)}
					</span>
					<p className="text-xs leading-5 text-grey-500">
						{t(`plans.${plan.name}.description`)}
					</p>
				</div>
				<div className="px-6 py-4">
					<Button
						size={mdDown ? 'xs' : 'default'}
						variant={cn(plan.name === 'trial' ? 'default' : 'outline')}
						onClick={() => {
							dispatch(setPrice({ id: plan.handlerId }));

							if (loggedIn) {
								return navigate(routes.checkout);
							}

							dispatch(
								openAuthModal({
									type: AUTH_MODAL_TYPES.SIGN_UP,
									redirect: routes.checkout
								})
							);
						}}
					>
						{plan.name === 'trial'
							? t('billing.button.startTrial')
							: t('billing.button.buyNow')}
					</Button>
				</div>
			</div>
		</div>
	);
}

export default PlanCard;
